import type { Schemas } from "@/types/api";

/**
 * Perfil de Cliente — espelho frontend do modelo multi-cliente white-label.
 *
 * UM core genérico, um binário, muitos perfis. O perfil é carregado de
 * GET /config/profile no arranque e diz que módulos/features estão ligados,
 * o branding e a nomenclatura desta instalação.
 *
 * Deve manter-se em sync com backend/app/config/client_profile.py (schema canónico) e web/src/lib/menu-paths.ts (módulo ↔ secção de menu).
 * Os ids de módulo e feature vêm de `modules.gen.ts`.
 *
 * Ver MULTI_CLIENTE.md (raiz do repo) para o desenho completo.
 */

// ─── Módulos canónicos ──────────────────────────────────────────────────────────
// GERADOS de `backend/app/config/modules.yml` (`make module-ids`): o catálogo é a fonte única e o `ModuleId` /
// `FeatureId` vêm de `modules.gen.ts`. Já não há lista à mão para divergir.
import { CORE_MODULES, OPTIONAL_MODULES, type ModuleId } from "./modules.gen";

export type { FeatureId, ModuleId } from "./modules.gen";

/** Todos os módulos que o produto conhece. */
export const ALL_MODULES: readonly ModuleId[] = [...CORE_MODULES, ...OPTIONAL_MODULES];

export type Branding = Schemas["Branding"];

export interface ClientProfile {
  id: string;
  modules: string[];
  features: string[];
  branding: Branding;
  nomenclature: Record<string, string>;
  /**
   * Ids dos modelos de rótulo desta instalação (`templates/rotulos/*`). Os modelos são
   * código e vão em todas as builds, por isso é isto que decide quais cada cliente vê.
   * Omitido/`null` = todos (instalações anteriores ao campo). Espelha o backend.
   */
  label_templates?: string[] | null;
}

/** Perfil de fábrica neutro — usado enquanto /config/profile não respondeu. */
export const DEFAULT_PROFILE: ClientProfile = {
  id: "default",
  modules: [...CORE_MODULES],
  features: [],
  branding: {
    name: "ERP",
    // Sem marca: este perfil é o que vale enquanto `/config/profile` não
    // respondeu, e apontar para os ficheiros de um cliente concreto punha o
    // logótipo dele no ecrã de qualquer outro durante esse instante.
    logo: "",
    favicon: "",
    brand: "default",
    color: null,
  },
  nomenclature: {},
};

/** Mapeia o `id` de uma secção de menu (menu-paths.ts) ao módulo canónico. */
export function sectionToModule(sectionId: string): ModuleId {
  // O único id desalinhado: a secção de menu "recursos-humanos" = módulo "hr".
  if (sectionId === "recursos-humanos") return "hr";
  // "dados-mestre" pertence ao módulo de produção: máquinas e acessórios são
  // chão de fábrica (entidades vivem no domínio producao). A matéria-prima
  // saiu daqui para "catalogo-artigos" quando `products` mudou de módulo.
  if (sectionId === "dados-mestre") return "producao";
  // A secção "Segurança" no menu é o módulo `security` (app do posto).
  if (sectionId === "seguranca") return "security";
  // A secção "Rastreabilidade" é o módulo `reciclados` — o id do menu é
  // história (nasceu antes do módulo ter nome próprio), o do módulo é a
  // estrutura do produto. Sem esta linha a secção caía como módulo
  // desconhecido e NUNCA era filtrada: apareceria a quem não a comprou.
  if (sectionId === "rastreabilidade") return "reciclados";
  // "terceiros" no menu chama-se "Clientes e Fornecedores" — id em português
  // corrente, módulo com o nome do domínio.
  if (sectionId === "clientes-fornecedores") return "terceiros";
  // A secção "Catálogo de Artigos" é o módulo `artigos` (products, productions).
  if (sectionId === "catalogo-artigos") return "artigos";
  // Separada do catálogo a 2026-09-02. Sem esta linha a secção resolveria para
  // um módulo inexistente e NUNCA seria filtrada — que é como um cliente sem
  // fabrico voltaria a ver ecrãs de produção.
  //
  // ⚠️ O backend fecha `/orders/factory-orders` com `require_any_module
  // ("comercial", "ordens_fabrico")` (basta ter um dos dois) — este mapa é
  // 1:1, só verifica `ordens_fabrico`. Não é um bug activo (Afisocks e EPEL
  // têm ambos `ordens_fabrico` hoje), mas um cliente hipotético com
  // `comercial` e sem `ordens_fabrico` ficaria bloqueado aqui apesar de a API
  // o deixar passar. Se isso vier a acontecer, o sítio certo é um mapa
  // `SHARED_SECTIONS` equivalente a `SHARED_ENTITIES` do backend — não gambiarra
  // aqui.
  if (sectionId === "ordens-fabrico") return "ordens_fabrico";
  return sectionId as ModuleId;
}
